// Client for the members data API (infra/members-api). The server verifies
// the Cognito ID token and custom:isSubscribed, and returns short-lived
// presigned links from a fixed allowlist. The browser sends only the market;
// it never names a bucket, key or prefix, and holds no AWS credentials.

// Set at build time (Amplify environment variable or .env.production) to the
// stack's ApiUrl output. Read per call so tests can set it.
const configuredBaseUrl = () => process.env.REACT_APP_MEMBERS_API_URL || '';

export class ManifestError extends Error {
  constructor(code, status) {
    super(code);
    this.name = 'ManifestError';
    this.code = code;
    this.status = status;
  }
}

export const manifestUrl = (baseUrl, market) =>
  `${baseUrl.replace(/\/+$/, '')}/members/manifest?market=${encodeURIComponent(market)}`;

/**
 * @param {{ market: 'sp500' | 'nasdaq' | 'dow', idToken: string | undefined,
 *           baseUrl?: string, fetchImpl?: typeof fetch }} options
 * @returns {Promise<{ market: string, expiresAt: string,
 *   datasets: Record<string, { url: string, key: string, lastModified: string } | null> }>}
 */
export const fetchManifest = async ({ market, idToken, baseUrl = configuredBaseUrl(), fetchImpl = fetch }) => {
  if (!baseUrl) throw new ManifestError('not_configured');
  if (!idToken) throw new ManifestError('missing_token');

  const res = await fetchImpl(manifestUrl(baseUrl, market), {
    headers: { Authorization: `Bearer ${idToken}` },
    cache: 'no-store',
  });

  let body = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok || !body?.success) {
    throw new ManifestError(body?.error || `http_${res.status}`, res.status);
  }
  return { ...body.data, datasets: httpsOnly(body.data.datasets) };
};

const isHttpsUrl = (value) => {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

// These links become hrefs and fetch targets. Anything that is not https
// (a javascript: URL from a compromised response, say) counts as missing.
const httpsOnly = (datasets = {}) =>
  Object.fromEntries(Object.entries(datasets).map(([name, dataset]) =>
    [name, dataset && isHttpsUrl(dataset.url) ? dataset : null]));
